<template>
    <div class="label label--half label--bottom-padding">
        {{ name }}
        <input v-if="type === 'text'"
            v-model="field.value"
            type="text"
            :class="['label__input', { 'label__input--active': field.value, 'label__disabled': !editable }]"
            :placeholder="placeholder"
            :readonly="!editable"
        >
        <input v-if="type === 'email'"
            :value="field.value"
            type="email"
            :class="['label__input', { 'label__input--active': field.value, 'label__disabled': !editable }]"
            :placeholder="placeholder"
            :readonly="!editable"
        >
        <input v-if="type === 'datetime'"
            :value="field.value ? format(field.value, 'yyyy-MM-dd') : null"
            type="date"
            :max="maxDate"
            :min="minDate"
            :class="['label__input', { 'label__input--active': field.value, 'label__disabled': !editable }]"
            :placeholder="placeholder"
            :readonly="!editable"
            @input="handleDateInput"
        >
        <multiselect v-if="type === 'list'"
            ref="multiselect"
            :value="getOptions()[chosenIndex]"
            :options="getOptions()"
            :allow-empty="false"
            :show-labels="false"
            :close-on-select="true"
            track-by="value"
            label="display"
            :placeholder="placeholder"
            :disabled="!editable"
            :class="['label__input multiselect', { 'label__input--active': field.value }]"
            :searchable="true"
            @input="onDropdownChange"
        >
            <template slot="option"
                slot-scope="props"
            >
                <span class="option">{{ props.option.display }}</span>
            </template>

            <template slot="caret">
                <i :class="['icon-chevron', {'icon-chevron--active': isOpen, 'icon-chevron--hidden': !editable}]" />
            </template>
        </multiselect>
    </div>
</template>

<script>
// 3rd party libs
import {
    mapGetters,
} from 'vuex';

// Utils
import {
    format,
} from '@shared/utils/dates';

export default {
    name: 'form-field',
    props: {
        name: {
            type: String,
            default: '',
        },
        placeholder: {
            type: String,
            default: '',
        },
        type: {
            type: String,
            default: 'text',
        },
        field: {
            type: Object,
            default: () => ({
                id: '',
                name: '',
                displayOrder: null,
                isOptional: false,
                fieldType: '',
                value: '',
            }),
        },
        value: {
            type: String,
            default: '',
        },
        options: {
            type: [Object, Array],
            default: () => ({}),
        },
        editable: {
            type: Boolean,
            default: true,
        },
        displayOrder: {
            type: Number,
            default: 0,
        },
    },
    data() {
        return {
            isOpen: false,
            dropdownSlots: {},
            format,
            chosenIndex: null,
        };
    },
    computed: {
        ...mapGetters('countries', ['countriesList']),

        maxDate() {
            const date = new Date();

            return this.options?.mustBePastDate === true
                ? `${date.getFullYear()}-${date.getMonth() + 1 < 10 ? '0' : ''}${date.getMonth() + 1}-${date.getDate() < 10 ? '0' : ''}${date.getDate()}`
                : '';
        },

        minDate() {
            const date = new Date();

            return this.options?.mustBeFutureDate === true
                ? `${date.getFullYear()}-${date.getMonth() + 1 < 10 ? '0' : ''}${date.getMonth() + 1}-${date.getDate() < 10 ? '0' : ''}${date.getDate()}`
                : '';
        },
    },
    methods: {
        onDropdownChange(dropdownItem) {
            this.$emit('input', {
                value: dropdownItem.value,
            });
            this.chosenIndex = dropdownItem.index;
        },

        getOptions() {
            return this.isCountryDropdown() ? [...this.countriesList].map((country, idx) => {
                // Preselect initial field value
                if (this.field?.value === country.value) {
                    this.chosenIndex = idx;
                }

                return {
                    value: country.value,
                    display: country.display,
                    index: idx,
                };
            }) : this.field?.options?.values?.map((val, idx) => {
                // Preselect initial field value
                if (this.field?.value === val) {
                    this.chosenIndex = idx;
                }

                return {
                    value: val,
                    display: val,
                    index: idx,
                };
            });

        },

        handleDateInput(ev) {
            if (ev.target.value.length > 10) {
                ev.target.value = format(this.field.value, 'yyyy-MM-dd');

                return;
            }
            this.$emit('input', {
                value: ev.target?.value?.length === 10 ? `${format(ev.target.value, 'standardDateTime')}+00:00` : ev.target.value,
            });
        },

        isCountryDropdown() {
            return Boolean(this.field.options?.isCountryList);
        },
    },
};
</script>

<style lang="scss" scoped>
input {
    font-size: 1.6rem;
    font-family: "Nunito";

    &:invalid {
        border-bottom: 2px solid $torch-red-color;
    }
}

.label__disabled {
    cursor: not-allowed;
}

.multiselect {
    display: flex;
    flex-direction: row-reverse;
    justify-content: space-between;

    .icon-chevron {
        transform: rotate(180deg);
        font-size: 1rem;
        margin-bottom: 0.7rem;

        &--active {
            transform: rotate(0deg);
            transition: all 0.3s;
        }

        &--hidden {
            visibility: hidden;
        }
    }

    ::v-deep .multiselect__placeholder {
        font-size: 1.6rem;
    }

    ::v-deep .multiselect__content-wrapper {
        width: inherit;
        margin-top: 3rem;
    }
}
</style>